/**
 * Pushmeldingen op dit apparaat, en hoe laat de Funda-ronde zich 's ochtends
 * meldt. Staat op de rondepagina (de mobiele app) en in Instellingen.
 *
 * Een melding geldt per apparaat: de telefoon en de laptop melden zich elk apart
 * aan. Op een iPhone kan dat alleen in MamiMap op het beginscherm; in Safari zelf
 * bestaat push niet. Daar staat dan in één zin hoe je hem erop zet.
 */
import { BellOff, BellRing } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api/endpoints';
import { usePushSleutel, useVangerInstellingen, useVangerStand } from '@/lib/api/queries';
import { abonneer, alsJson, huidigAbonnement, pushOndersteuning } from '@/lib/push';
import { Link, ROUTES } from '@/lib/router';

type Toestand = 'onbekend' | 'aan' | 'uit';

export function PushInstelling() {
  const sleutel = usePushSleutel();
  const ondersteuning = pushOndersteuning();
  const [toestand, zetToestand] = useState<Toestand>('onbekend');
  const [bezig, zetBezig] = useState<'aan' | 'uit' | 'test' | null>(null);
  const [melding, zetMelding] = useState<{ tekst: string; fout: boolean } | null>(null);

  useEffect(() => {
    let weg = false;
    void (async () => {
      const abonnement = await huidigAbonnement().catch(() => null);
      if (!abonnement) {
        if (!weg) zetToestand('uit');
        return;
      }
      const bekend = await api
        .pushStand(abonnement.endpoint)
        .then((uit) => uit.geabonneerd)
        .catch(() => false);
      if (!weg) zetToestand(bekend ? 'aan' : 'uit');
    })();
    return () => {
      weg = true;
    };
  }, []);

  if (ondersteuning === 'beginscherm') {
    return (
      <p className="text-[13px] text-ink-soft">
        Op een iPhone werken meldingen alleen vanaf het beginscherm: tik in Safari op Deel, dan Zet
        op beginscherm, open MamiMap daar en kom hier terug.
      </p>
    );
  }
  if (ondersteuning === 'nee') {
    return <p className="text-[13px] text-ink-soft">Deze browser kent geen pushmeldingen.</p>;
  }
  if (sleutel.data && sleutel.data.sleutel === null) {
    return (
      <p className="text-[13px] text-ink-soft">
        Pushmeldingen zijn op deze server niet ingesteld (VAPID-sleutels). Meldingen gaan als mail.
      </p>
    );
  }

  async function zetAan() {
    const publiek = sleutel.data?.sleutel;
    if (!publiek) return;
    zetBezig('aan');
    zetMelding(null);
    try {
      const abonnement = await abonneer(publiek);
      await api.pushAbonneer(alsJson(abonnement));
      zetToestand('aan');
      zetMelding({ tekst: 'Meldingen staan aan op dit apparaat.', fout: false });
    } catch (e) {
      zetMelding({ tekst: e instanceof Error ? e.message : 'Aanzetten mislukte.', fout: true });
    } finally {
      zetBezig(null);
    }
  }

  async function zetUit() {
    zetBezig('uit');
    zetMelding(null);
    try {
      const abonnement = await huidigAbonnement();
      if (abonnement) {
        await api.pushAfmelden(abonnement.endpoint);
        await abonnement.unsubscribe();
      }
      zetToestand('uit');
    } catch (e) {
      zetMelding({ tekst: e instanceof Error ? e.message : 'Uitzetten mislukte.', fout: true });
    } finally {
      zetBezig(null);
    }
  }

  async function test() {
    zetBezig('test');
    zetMelding(null);
    try {
      const uit = await api.pushTest();
      zetMelding({
        tekst:
          uit.verzonden > 0
            ? `Verstuurd naar ${uit.verzonden} van ${uit.apparaten} apparaten.`
            : 'Geen apparaat bereikt.',
        fout: uit.verzonden === 0,
      });
    } catch (e) {
      zetMelding({ tekst: e instanceof Error ? e.message : 'Testen mislukte.', fout: true });
    } finally {
      zetBezig(null);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {toestand === 'aan' ? (
          <button
            type="button"
            className="knop-stil"
            onClick={() => void zetUit()}
            disabled={bezig !== null}
          >
            <BellOff className="h-4 w-4" aria-hidden="true" />
            {bezig === 'uit' ? 'Bezig' : 'Meldingen op dit apparaat uitzetten'}
          </button>
        ) : (
          <button
            type="button"
            className="knop"
            onClick={() => void zetAan()}
            disabled={bezig !== null || toestand === 'onbekend' || !sleutel.data?.sleutel}
          >
            <BellRing className="h-4 w-4" aria-hidden="true" />
            {bezig === 'aan' ? 'Bezig' : 'Meldingen op dit apparaat aanzetten'}
          </button>
        )}
        <button
          type="button"
          className="knop-stil"
          onClick={() => void test()}
          disabled={bezig !== null || toestand !== 'aan'}
        >
          {bezig === 'test' ? 'Bezig' : 'Testmelding'}
        </button>
      </div>
      {melding ? (
        <p className={`mt-2 text-[12px] ${melding.fout ? 'text-brick' : 'text-ink-soft'}`}>
          {melding.tekst}
        </p>
      ) : null}
    </div>
  );
}

/** `metLink`: in Instellingen staat er een verwijzing naar de rondepagina bij. */
export function RondeInstelling({ metLink = true }: { metLink?: boolean }) {
  const stand = useVangerStand();
  const opslaan = useVangerInstellingen();
  const [tijd, zetTijd] = useState<string | null>(null);
  const huidig = stand.data?.ochtend ?? '08:00';
  const waarde = tijd ?? huidig;

  if (stand.data?.staat === 'uit') {
    return (
      <p className="text-[13px] text-ink-soft">
        De vanger staat uit op deze omgeving.{' '}
        {metLink ? (
          <Link naar={ROUTES.ronde} className="text-accent underline">
            Funda-ronde
          </Link>
        ) : null}
      </p>
    );
  }

  return (
    <div>
      <label className="veldnaam" htmlFor="ochtendtijd">
        Ochtendmelding
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <input
          id="ochtendtijd"
          type="time"
          className="veld cijfers w-[120px]"
          value={waarde}
          onChange={(e) => zetTijd(e.target.value)}
        />
        <button
          type="button"
          className="knop-stil"
          disabled={opslaan.isPending || waarde === huidig || !/^\d\d:\d\d$/.test(waarde)}
          onClick={() => opslaan.mutate({ ochtend: waarde }, { onSuccess: () => zetTijd(null) })}
        >
          {opslaan.isPending ? 'Bezig' : 'Bewaren'}
        </button>
        {metLink ? (
          <Link naar={ROUTES.ronde} className="text-[13px] text-accent underline">
            Funda-ronde
          </Link>
        ) : null}
      </div>
      {opslaan.error ? <p className="veldfout">{opslaan.error.message}</p> : null}
    </div>
  );
}
